import type { PayoutSchemeConfig, CellMode } from '../types';
import { getBoxNeighborCount, getTotalCells } from '../types';
import { getDefaultQuarterPayouts } from '../components';

/**
 * Convert percentage-based payouts to dollar amounts
 */
export const convertPercentagesToDollars = (
  percentages: Record<string, number>,
  payoutPot: number
): Record<string, number> => {
  const dollars: Record<string, number> = {};
  for (const [key, pct] of Object.entries(percentages)) {
    dollars[key] = Math.round(payoutPot * pct * 100) / 100; // Round to 2 decimal places
  }
  return dollars;
};

/**
 * Get default dollar payouts for a given configuration
 */
export const getDefaultDollarPayouts = (
  quarters: number,
  pricePerSquare: number,
  profitPercent: number,
  cellMode: CellMode = 'standard'
): Record<string, number> => {
  const totalCells = getTotalCells(cellMode);
  const totalPot = pricePerSquare * totalCells;
  const payoutPot = totalPot * (1 - profitPercent / 100);
  const percentages = getDefaultQuarterPayouts(quarters);
  return convertPercentagesToDollars(percentages, payoutPot);
};

export interface PayoutValidationResult {
  isValid: boolean;
  remainingDollars: number;
  isOverage: boolean;
  payoutPot: number;
  allocatedDollars: number;
}

/**
 * Calculate payout validation - checks if all payouts are properly allocated
 */
export function calculatePayoutValidation(
  pricePerSquare: number,
  profitPercent: number,
  payoutConfig: PayoutSchemeConfig,
  quarters: number,
  cellMode: CellMode
): PayoutValidationResult {
  const totalCells = getTotalCells(cellMode);
  const totalPot = pricePerSquare * totalCells;
  const profitAmount = totalPot * (profitPercent / 100);
  const payoutPot = totalPot - profitAmount;

  const quarterTotal = Object.values(payoutConfig.quarterPayouts || {}).reduce((sum, amount) => sum + amount, 0);
  const wrongWayTotal = Object.values(payoutConfig.quarterWrongWayPayouts || {}).reduce((sum, amount) => sum + amount, 0);
  const plusNTotal = Object.values(payoutConfig.quarterPlusNPayouts || {}).reduce((sum, amount) => sum + amount, 0);
  const plusNWrongWayTotal = Object.values(payoutConfig.quarterPlusNWrongWayPayouts || {}).reduce((sum, amount) => sum + amount, 0);
  const customTotal = (payoutConfig.customCategories || []).reduce((sum, cat) => {
    const baseAmount = cat.payoutAmount || 0;
    return sum + (cat.wrongWayEnabled ? baseAmount * 2 : baseAmount);
  }, 0);

  // Neighbor payouts calculation
  const neighborConfig = payoutConfig.neighborPayouts;
  const neighborDirections = neighborConfig?.directions || [];
  const neighborTotal = (() => {
    if (!neighborConfig?.enabled) return 0;
    const directionCount = neighborDirections.length || 0;
    // Get quarter count for calculation
    const quarterKeys = quarters === 2 ? ['1H', '2H'] : quarters === 3 ? ['P1', 'P2', 'P3'] : ['Q1', 'Q2', 'Q3', 'Q4'];
    if (neighborConfig.usePerQuarterAmounts && neighborConfig.quarterAmounts) {
      return quarterKeys.reduce(
        (sum, key) => sum + (neighborConfig.quarterAmounts?.[key] || 0) * directionCount,
        0
      );
    }
    return (neighborConfig.globalAmount || 0) * directionCount * quarterKeys.length;
  })();

  // Bad numbers payouts calculation
  const badNumbersConfig = payoutConfig.badNumbersConfig;
  const badNumbersTotal = (() => {
    if (!badNumbersConfig?.enabled) return 0;
    // Use badCombinations if available, otherwise fall back to legacy badDigits
    if (badNumbersConfig.badCombinations && badNumbersConfig.badCombinations.length > 0) {
      return badNumbersConfig.badCombinations.length * (badNumbersConfig.payoutAmount || 0);
    }
    // Legacy fallback
    const badDigitCount = badNumbersConfig.badDigits?.length || 0;
    const badSquareCount = badDigitCount * badDigitCount;
    return badSquareCount * (badNumbersConfig.payoutAmount || 0);
  })();

  // Box neighbor payouts calculation
  const boxNeighborConfig = payoutConfig.boxNeighborPayouts;
  const boxNeighborTotal = (() => {
    if (!boxNeighborConfig?.enabled) return 0;
    const neighborCount = getBoxNeighborCount(boxNeighborConfig.boxSize);
    const quarterKeys = quarters === 2 ? ['1H', '2H'] : quarters === 3 ? ['P1', 'P2', 'P3'] : ['Q1', 'Q2', 'Q3', 'Q4'];
    if (boxNeighborConfig.usePerQuarterAmounts && boxNeighborConfig.quarterAmounts) {
      return quarterKeys.reduce(
        (sum, key) => sum + (boxNeighborConfig.quarterAmounts?.[key] || 0) * neighborCount,
        0
      );
    }
    return (boxNeighborConfig.globalAmount || 0) * neighborCount * quarterKeys.length;
  })();

  const allocatedDollars = quarterTotal + wrongWayTotal + plusNTotal + plusNWrongWayTotal + customTotal + neighborTotal + badNumbersTotal + boxNeighborTotal;
  const remainingDollars = payoutPot - allocatedDollars;
  const isValid = Math.abs(remainingDollars) <= 0.01;

  return {
    isValid,
    remainingDollars,
    isOverage: remainingDollars < -0.01,
    payoutPot,
    allocatedDollars,
  };
}
